import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

interface DomOverrideRow {
  selector: string;
  page: string;
  tag_name: string | null;
  text_content: string | null;
  styles: Json;
}

const META_PREFIX = "__";

const normalizeText = (s: string | null | undefined) =>
  (s ?? "").replace(/\s+/g, " ").trim().slice(0, 500);

const resolveElement = (override: DomOverrideRow): HTMLElement | null => {
  // 1. Try the saved selector first (fast path)
  try {
    const direct = document.querySelector(override.selector) as HTMLElement | null;
    if (direct) return direct;
  } catch {
    /* invalid selector → fall through */
  }

  const styles = (typeof override.styles === "object" && override.styles && !Array.isArray(override.styles)
    ? (override.styles as Record<string, string>)
    : {}) as Record<string, string>;

  const tag = (override.tag_name || "").toLowerCase();
  if (!tag) return null;

  const candidates = Array.from(document.querySelectorAll(tag)) as HTMLElement[];
  if (candidates.length === 0) return null;

  const wantedSrc = styles.__src;
  const wantedAlt = styles.__alt;
  const wantedId = styles.__id;
  const wantedText = normalizeText(override.text_content || styles.__textContent);

  // 2. Match by anchor hints (most reliable for images / unique nodes)
  if (wantedId) {
    const byId = candidates.find((c) => c.id === wantedId);
    if (byId) return byId;
  }
  if (wantedSrc && tag === "img") {
    const bySrc = candidates.find((c) => (c as HTMLImageElement).src === wantedSrc || (c as HTMLImageElement).getAttribute("src") === wantedSrc);
    if (bySrc) return bySrc;
  }
  if (wantedAlt && tag === "img") {
    const byAlt = candidates.find((c) => (c as HTMLImageElement).alt === wantedAlt);
    if (byAlt) return byAlt;
  }

  // 3. Match by text content (for buttons, headings, links, etc.)
  if (wantedText) {
    const byText = candidates.find((c) => normalizeText(c.textContent) === wantedText);
    if (byText) return byText;
    // looser contains match
    const byContains = candidates.find((c) => normalizeText(c.textContent).includes(wantedText.slice(0, 60)));
    if (byContains) return byContains;
  }

  return null;
};

const applyOverride = (override: DomOverrideRow) => {
  const el = resolveElement(override);
  if (!el) return false;

  const styles =
    typeof override.styles === "object" && override.styles && !Array.isArray(override.styles)
      ? (override.styles as Record<string, string>)
      : {};

  Object.entries(styles).forEach(([prop, value]) => {
    if (!value) return;
    if (prop === "__textContent") {
      if (el.textContent !== value) el.textContent = value;
      return;
    }
    // skip anchor-hint metadata
    if (prop.startsWith(META_PREFIX)) return;
    el.style.setProperty(prop, value, "important");
  });

  return true;
};

const DesignDomOverridesApplier = () => {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    let observer: MutationObserver | null = null;
    let timeoutIds: number[] = [];
    let rafId = 0;

    const clearRetries = () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      timeoutIds = [];
    };

    const loadAndApply = async () => {
      const { data, error } = await supabase
        .from("design_dom_overrides")
        .select("selector,page,tag_name,text_content,styles")
        .in("page", ["all", location.pathname]);

      if (cancelled || error || !data) return;

      const overrides = data as unknown as DomOverrideRow[];
      let scheduled = false;
      const applyAll = () => {
        if (scheduled) return;
        scheduled = true;
        rafId = window.requestAnimationFrame(() => {
          scheduled = false;
          overrides.forEach(applyOverride);
        });
      };

      applyAll();
      [80, 250, 700, 1500, 3000].forEach((delay) => {
        timeoutIds.push(window.setTimeout(applyAll, delay));
      });

      observer = new MutationObserver(applyAll);
      observer.observe(document.body, { childList: true, subtree: true });
    };

    loadAndApply();

    return () => {
      cancelled = true;
      clearRetries();
      if (rafId) window.cancelAnimationFrame(rafId);
      observer?.disconnect();
    };
  }, [location.pathname]);

  return null;
};

export default DesignDomOverridesApplier;
